// Libs
import classNames from 'classnames/bind';
import { Grid2, IconButton } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { IFormEstimateTemplate } from '~/utils/interface/estimateTemplate';
import { ISelectMaterial } from '~/utils/interface/material';
import { formatInputDecimal } from '~/utils/helper/common';
// Styles, images, icons
import styles from './EstimateTemplateMaterials.module.scss';
import { icons } from '~/assets';

type Props = {
  index: number;
  options?: ISelectMaterial[];
  isLoading?: boolean;
  onRemoveItem: (index: number) => void;
};

const cx = classNames.bind(styles);

const EstimateTemplateMaterials = (props: Props) => {
  //#region Destructuring Props
  const { index, options, isLoading, onRemoveItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimateTemplate']);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IFormEstimateTemplate>();
  const quantity = watch(`estimateMaterials.${index}.quantity`);
  const price = watch(`estimateMaterials.${index}.price`);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue(`estimateMaterials.${index}.total`, String(Number(quantity) * Number(price)) || EMPTY_STRING, {
      shouldValidate: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, quantity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOnchangeMaterial = (value: ISelectMaterial) => {
    setValue(`estimateMaterials.${index}.materialId`, value?.value || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateMaterials.${index}.price`, value?.price || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateMaterials.${index}.cost`, value?.cost || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateMaterials.${index}.quantity`, value?.quantity || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateMaterials.${index}.description`, value?.description || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  //#endregion Handle Function

  return (
    <Grid2
      component='div'
      id='estimateTemplateMaterialsComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2
        size={'grow'}
        container
        rowSpacing={'12px'}
        columnSpacing={'12px'}
        className={cx(index !== DEFAULT_NUMBER_ZERO ? 'item' : EMPTY_STRING)}
      >
        <Grid2 size={'grow'}>
          <Controller
            name={`estimateMaterials.${index}.materialId`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseSelect
                label={t('field_material_name_label')}
                value={value || EMPTY_STRING}
                onChange={(optionValue) => handleOnchangeMaterial(optionValue)}
                options={options || []}
                isLoading={isLoading}
                errorMessage={errors?.estimateMaterials?.[index]?.materialId?.message}
                required
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateMaterials.${index}.description`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_material_description_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.estimateMaterials?.[index]?.description?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateMaterials.${index}.price`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_material_price_label')}
                value={value}
                required
                onChange={(event) => {
                  const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                  const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                  onChange(number);
                }}
                errorMessage={errors?.estimateMaterials?.[index]?.price?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateMaterials.${index}.cost`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_material_cost_label')}
                value={value}
                required
                onChange={(event) => {
                  const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                  const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                  onChange(number);
                }}
                errorMessage={errors?.estimateMaterials?.[index]?.cost?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateMaterials.${index}.quantity`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_material_quantity_label')}
                value={value}
                required
                onChange={(event) => {
                  onChange(formatInputDecimal(event.target.value));
                }}
                errorMessage={errors?.estimateMaterials?.[index]?.quantity?.message}
              />
            )}
          />
        </Grid2>
      </Grid2>

      <Grid2 size='auto' className='flex justify-center items-center'>
        <IconButton className={cx('closeField')} onClick={() => onRemoveItem(index)}>
          <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>
      </Grid2>
    </Grid2>
  );
};

export default EstimateTemplateMaterials;
